import {
  Box,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { Methodology } from '@services/auditService';

interface MethodologyEditorProps {
  /** Current value; null/undefined = no methodology recorded yet. */
  value: Methodology | null | undefined;
  onChange: (next: Methodology) => void;
  /** Collapsed by default unless the existing value is non-empty. */
  defaultExpanded?: boolean;
  disabled?: boolean;
}

/**
 * MethodologyEditor (REV-204)
 *
 * Edits the four-field methodology object (tools, techniques, at_used,
 * steps_to_reproduce) that rides along with a TestEvaluation verdict.
 *
 * UX: one TextField per field; one item per line. Empty lines and
 * whitespace are stripped on conversion back to string[].
 */
export default function MethodologyEditor({
  value,
  onChange,
  defaultExpanded,
  disabled = false,
}: MethodologyEditorProps) {
  const { t } = useTranslation();

  const toText = (arr: string[] | undefined) => (arr && arr.length ? arr.join('\n') : '');
  const toArray = (text: string): string[] =>
    text
      .split('\n')
      .map((s) => s.trim())
      .filter(Boolean);

  const update = (field: keyof Methodology, text: string) => {
    const arr = toArray(text);
    const next: Methodology = { ...(value || {}) };
    if (arr.length === 0) {
      delete next[field];
    } else {
      next[field] = arr;
    }
    onChange(next);
  };

  const anyContent =
    !!value &&
    ((value.tools && value.tools.length > 0) ||
      (value.techniques && value.techniques.length > 0) ||
      (value.at_used && value.at_used.length > 0) ||
      (value.steps_to_reproduce && value.steps_to_reproduce.length > 0));

  return (
    <Accordion
      defaultExpanded={defaultExpanded ?? anyContent}
      disableGutters
      sx={{ boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="subtitle2">
            {t('methodology.title', 'Testing methodology (optional)')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t(
              'methodology.subtitle',
              'Document HOW this verdict was produced — tools, techniques, AT, and steps. One item per line.'
            )}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <TextField
            size="small"
            multiline
            minRows={2}
            maxRows={5}
            label={t('methodology.tools', 'Tools used')}
            helperText={t(
              'methodology.toolsHelper',
              'e.g. axe-core@4.10, manual review, Accessibility Insights'
            )}
            value={toText(value?.tools)}
            onChange={(e) => update('tools', e.target.value)}
            disabled={disabled}
            fullWidth
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            maxRows={5}
            label={t('methodology.techniques', 'Techniques applied')}
            helperText={t(
              'methodology.techniquesHelper',
              'WCAG technique ids (H37, G94) or free-form descriptions'
            )}
            value={toText(value?.techniques)}
            onChange={(e) => update('techniques', e.target.value)}
            disabled={disabled}
            fullWidth
          />
          <TextField
            size="small"
            multiline
            minRows={2}
            maxRows={5}
            label={t('methodology.atUsed', 'Assistive technology used')}
            helperText={t(
              'methodology.atUsedHelper',
              'e.g. VoiceOver 17 on iOS, NVDA 2024.1 on Firefox, JAWS 2024'
            )}
            value={toText(value?.at_used)}
            onChange={(e) => update('at_used', e.target.value)}
            disabled={disabled}
            fullWidth
          />
          <TextField
            size="small"
            multiline
            minRows={3}
            maxRows={8}
            label={t('methodology.steps', 'Steps to reproduce')}
            helperText={t(
              'methodology.stepsHelper',
              'Ordered steps an independent reviewer could replay. One step per line.'
            )}
            value={toText(value?.steps_to_reproduce)}
            onChange={(e) => update('steps_to_reproduce', e.target.value)}
            disabled={disabled}
            fullWidth
          />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
